import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { Bot } from 'lucide-react';
import { BUDDY_CONFIG } from 'config/buddy/config';
import { BuddyFace } from 'config/buddy/utils';
import { useTheme } from 'components/core/theme-provider';
import { pickKaomojiForWorkingStatusLabel } from '../utils/kaomoji';
import { WORKING_STATUS_FADE_MS } from '../utils/working-status-messages';
import { KLACKS_DWELL_MS, getGenerationDeck } from '../utils/klacks-lines';
import { useAgentWorkingLabel } from './AgentWorkingCursor';
import { KlacksDrop } from './KlacksDrop';
import { WorkingKaomoji } from './WorkingKaomoji';

interface AssistantAvatarProps {
  agentId?: string | null;
  agentLogoUrl?: string | null;
  className?: string;
}

/** Same icon as the agent selector tab: Buddy's face for Buddy / no agent, else the agent's logo. */
export const AssistantAvatar = ({ agentId, agentLogoUrl, className }: AssistantAvatarProps) => {
  const { theme } = useTheme();
  const isBuddy = !agentId || agentId === BUDDY_CONFIG.agentId;
  const wrapperClassName = cn(
    'inline-flex size-8 shrink-0 items-center justify-center [&_svg]:size-full',
    className
  );

  if (isBuddy) {
    return (
      <span role="img" aria-label="Buddy" className={wrapperClassName}>
        <BuddyFace haloId="klaxHaloAssistantAvatar" />
      </span>
    );
  }

  if (agentLogoUrl) {
    return (
      <img
        src={agentLogoUrl}
        alt="Agent"
        className={cn('size-8 shrink-0 rounded-full object-cover', className)}
      />
    );
  }

  return (
    <span role="img" aria-label="Agent" className={cn(wrapperClassName, 'p-1.5')}>
      <Bot className={theme === 'dark' ? 'text-true-white' : 'text-grey-900'} />
    </span>
  );
};

interface MessageWorkingIndicatorProps {
  /** When set (e.g. compaction), show this label instead of the Klacks lines. */
  label?: string;
  /** Lets a remounted row continue the same line deck within one reply. */
  generationId?: string;
  agentName?: string | null;
  /** Appended to the visible text, e.g. " · 0:42" for a long wait. */
  suffix?: string;
}

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Working row: a Klacks drop, a rotating "this is easy" line and its ASCII companion. */
export const MessageWorkingIndicator = ({
  label: fixedLabel,
  generationId = 'pending',
  agentName,
  suffix = '',
}: Readonly<MessageWorkingIndicatorProps> = {}) => {
  const { t } = useTranslation();
  const workingLabel = useAgentWorkingLabel(agentName);
  const [line, setLine] = useState(() => {
    const { deck, startedAt } = getGenerationDeck(generationId);
    return deck.next(Date.now() - startedAt);
  });
  const [visible, setVisible] = useState(true);
  const wasFixedRef = useRef(false);

  useEffect(() => {
    if (fixedLabel) {
      wasFixedRef.current = true;
      setVisible(true);
      return;
    }

    const { deck, startedAt } = getGenerationDeck(generationId);
    const isInstant = prefersReducedMotion();
    const timers: number[] = [];

    const showNext = () => {
      const next = deck.next(Date.now() - startedAt);
      setLine(next);
      setVisible(true);
      scheduleNext(next.kind);
    };

    function scheduleNext(kind: keyof typeof KLACKS_DWELL_MS) {
      timers.push(
        window.setTimeout(() => {
          if (isInstant) {
            showNext();
            return;
          }
          setVisible(false);
          timers.push(window.setTimeout(showNext, WORKING_STATUS_FADE_MS));
        }, KLACKS_DWELL_MS[kind])
      );
    }

    // After a fixed label clears, continue with the deck's next line, not the old one.
    if (wasFixedRef.current) {
      wasFixedRef.current = false;
      showNext();
    } else {
      scheduleNext(line.kind);
    }

    return () => timers.forEach((timer) => window.clearTimeout(timer));
    // `line` only seeds the first dwell; the timers own rotation after that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedLabel, generationId]);

  const text = fixedLabel || t(line.i18nKey, { defaultValue: line.defaultValue });
  const ascii = fixedLabel ? pickKaomojiForWorkingStatusLabel(fixedLabel) : line.ascii;
  const fadeClassName = cn(
    'transition-opacity ease-in-out motion-reduce:transition-none',
    visible ? 'opacity-100' : 'opacity-0'
  );
  const fadeStyle = { transitionDuration: `${WORKING_STATUS_FADE_MS}ms` };

  return (
    <div className="flex min-h-8 min-w-0 items-center gap-2 self-start">
      <span className="sr-only">{fixedLabel || workingLabel}</span>
      <div className="flex min-w-0 items-center gap-2" aria-hidden>
        <KlacksDrop key={fixedLabel ? `label:${fixedLabel}` : line.id} />
        <p
          className={cn(
            'working-label line-clamp-2 min-h-[2lh] w-fit text-sm font-medium leading-body sm:min-h-[1lh]',
            fadeClassName
          )}
          style={fadeStyle}
        >
          {text}
          {suffix}
        </p>
        <WorkingKaomoji kaomoji={ascii} className={fadeClassName} style={fadeStyle} />
      </div>
    </div>
  );
};

/** Three-dot wave shown beside the assistant logo while waiting for a response. */
// export const MessageLoadingDots = () => (
//   <>
//     <style>{loadingWaveKeyframes}</style>
//     <div className="flex h-6 items-center gap-1.5 px-1 py-2" aria-hidden>
//       {[0, 1, 2].map((index) => (
//         <span
//           key={index}
//           className="inline-block h-2 w-2 rounded-full bg-primary-600"
//           style={{
//             animation: 'message-loading-wave 1.6s ease-in-out infinite',
//             animationDelay: `${index * 0.18}s`,
//           }}
//         />
//       ))}
//     </div>
//   </>
// );

export const MessageLoadingDots = MessageWorkingIndicator;

export const MessageLoading = () => {
  const { t } = useTranslation();

  return (
    <div
      className="flex items-center justify-start gap-4"
      role="status"
      aria-live="polite"
      aria-label={t('ASSISTANT_IS_THINKING')}
    >
      <AssistantAvatar />
      <MessageWorkingIndicator />
    </div>
  );
};
